// Sidebar — 1:1 port of legacy src/shell/Sidebar.jsx.
// - Role-aware nav (menuForRole from ./menu.ts).
// - Active highlight via useLocation().
// - Collapse toggle via stores/sidebar.ts.
// - All styling replicates legacy tokens: `--sidebar-w`, `--sidebar-w-collapsed`,
//   `--sidebar-bg`, `--sidebar-fg-muted`, `--sidebar-active-bg`, etc. (see tokens.css).
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { hasPermission, useAuthStore } from '../stores/auth.js';
import { useSidebarStore } from '../stores/sidebar.js';
import { useCargoProblemAttention } from '../features/cargo-problems/index.js';
import { Icon, UnreadBadge } from '../ui/index.js';
import { findActiveMenuMatch, menuForUser, type MenuItem } from './menu.js';

interface NavItemProps {
  item: MenuItem;
  label: string;
  active: boolean;
  collapsed: boolean;
  onClick: () => void;
  badgeCount?: number;
}

function NavItem({ item, label, active, collapsed, onClick, badgeCount = 0 }: NavItemProps) {
  const [hover, setHover] = useState(false);
  const base = {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    height: 32,
    padding: collapsed ? 0 : '0 12px',
    margin: '1px 4px',
    justifyContent: collapsed ? 'center' : 'flex-start',
    borderRadius: 'var(--r-2)',
    color: 'var(--sidebar-fg-muted)',
    fontSize: 13,
    cursor: 'pointer',
    position: 'relative' as const,
    whiteSpace: 'nowrap' as const,
    transition: 'background var(--dur-1) var(--ease-out), color var(--dur-1) var(--ease-out)',
  };
  const activeStyle = active
    ? {
        background: 'var(--sidebar-active-bg)',
        color: 'var(--sidebar-active-fg)',
        boxShadow: 'inset 2px 0 0 var(--sidebar-rail)',
        fontWeight: 500,
      }
    : null;
  const hoverStyle =
    hover && !active ? { background: 'var(--sidebar-bg-hover)', color: 'var(--sidebar-fg)' } : null;
  return (
    <div
      style={{ ...base, ...activeStyle, ...hoverStyle }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onClick}
      title={collapsed ? label : undefined}
      role="button"
      aria-current={active ? 'page' : undefined}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <Icon name={item.icon} size={16} />
      {!collapsed && (
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
      )}
      {badgeCount > 0 ? (
        <UnreadBadge
          unreadCount={badgeCount}
          isMarkedUnread={false}
          accessibleNameKeys={{ count: 'attentionCount', capped: 'attentionCapped' }}
        />
      ) : null}
    </div>
  );
}

export interface SidebarProps {
  // Optional overrides so MobileNav can force expanded + custom close action.
  forceExpanded?: boolean;
  onToggleOverride?: () => void;
  // WHY: MobileNav passes `onNavigate` so activating any menu item
  //   closes the slide-over drawer — including a no-op tap on the
  //   current route. Desktop callers omit this and behavior is
  //   unchanged.
  onNavigate?: () => void;
}

export function Sidebar({ forceExpanded, onToggleOverride, onNavigate }: SidebarProps = {}) {
  const { t } = useTranslation('shell');
  const user = useAuthStore((s) => s.user);
  const permissions = useAuthStore((s) => s.permissions);
  const storeCollapsed = useSidebarStore((s) => s.collapsed);
  const storeToggle = useSidebarStore((s) => s.toggle);
  const loc = useLocation();
  const navigate = useNavigate();
  const canReadCargoProblems = hasPermission(permissions, 'cargoProblems.read');
  const attention = useCargoProblemAttention(canReadCargoProblems);

  const collapsed = forceExpanded ? false : storeCollapsed;
  const onToggle = onToggleOverride ?? storeToggle;
  const groups = menuForUser(user, permissions);
  const activeRoute = findActiveMenuMatch(loc.pathname, groups)?.item.route ?? null;
  const w = collapsed ? 'var(--sidebar-w-collapsed)' : 'var(--sidebar-w)';

  return (
    <aside
      className={collapsed ? 'collapsed' : ''}
      style={{
        width: w,
        flex: 'none',
        background: 'var(--sidebar-bg)',
        color: 'var(--sidebar-fg-muted)',
        display: 'flex',
        flexDirection: 'column',
        borderRight: '1px solid var(--sidebar-border)',
        height: '100%',
        position: 'relative',
        transition:
          'width var(--dur-2) var(--ease-out), background var(--dur-1) var(--ease-out), color var(--dur-1) var(--ease-out), border-color var(--dur-1) var(--ease-out)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          height: 'var(--topbar-h)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: collapsed ? '0' : '0 16px',
          justifyContent: collapsed ? 'center' : 'flex-start',
          borderBottom: '1px solid var(--sidebar-border)',
        }}
      >
        <svg width="24" height="24" viewBox="0 0 40 40" style={{ color: 'var(--sidebar-brand)' }}>
          <rect
            x="0.75"
            y="0.75"
            width="38.5"
            height="38.5"
            rx="4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
          />
          <path
            d="M 8 30 L 14 12 L 20 22 L 26 12 L 32 30"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <circle cx="20" cy="22" r="2" fill="var(--accent-1)" />
        </svg>
        {!collapsed && (
          <span
            style={{
              fontFamily: 'var(--font-ui)',
              fontSize: 15,
              fontWeight: 600,
              color: 'var(--sidebar-brand)',
              letterSpacing: '-.01em',
            }}
          >
            magimp
          </span>
        )}
      </div>

      <nav style={{ flex: 1, overflowY: 'auto', padding: '8px 8px 12px' }}>
        {groups.map((g) => (
          <div key={g.id} style={{ marginBottom: 14 }}>
            {!collapsed && (
              <div
                style={{
                  fontSize: 10,
                  letterSpacing: '.1em',
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  color: 'var(--sidebar-fg-dim)',
                  padding: '6px 12px 4px',
                }}
              >
                {t(g.label)}
              </div>
            )}
            {collapsed && (
              <div
                style={{
                  height: 6,
                  margin: '6px 12px',
                  borderTop: '1px solid var(--sidebar-border)',
                }}
              />
            )}
            {g.items.map((it) => (
              <NavItem
                key={it.id}
                item={it}
                label={t(it.label)}
                collapsed={collapsed}
                active={it.route === activeRoute}
                badgeCount={it.id === 'cargoProblems' ? (attention.data?.menuCount ?? 0) : 0}
                onClick={() => {
                  // INVARIANT: navigate first, then notify the host
                  //   shell. `onNavigate` MUST fire even when the
                  //   target route equals the current one — the
                  //   navigate() call is a no-op there but MobileNav
                  //   still needs to close its drawer.
                  navigate(it.route);
                  onNavigate?.();
                }}
              />
            ))}
          </div>
        ))}
      </nav>

      <div
        style={{
          borderTop: '1px solid var(--sidebar-border)',
          padding: '8px',
          display: 'flex',
          justifyContent: collapsed ? 'center' : 'flex-end',
        }}
      >
        <button
          onClick={onToggle}
          style={{
            width: 32,
            height: 32,
            borderRadius: 'var(--r-1)',
            color: 'var(--sidebar-fg-muted)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'var(--sidebar-bg-hover)';
            e.currentTarget.style.color = 'var(--sidebar-fg)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = 'var(--sidebar-fg-muted)';
          }}
          title={collapsed ? t('sidebar.expand') : t('sidebar.collapse')}
          aria-label={collapsed ? t('sidebar.expandMenu') : t('sidebar.collapseMenu')}
        >
          <Icon name={collapsed ? 'panel-left-open' : 'panel-left-close'} size={16} />
        </button>
      </div>
    </aside>
  );
}
